// Required fields of a position for Ange.Bot (spec 8a): the same list as Ange.Bot's field matrix, with Ange.Bot's
// gate as the rule for "required" (form, substances with name, dose and unit, quantity as a number). Packaging only
// (Lohnverpackung, F13) has no recipe: quantity of the customer's bulk and the portion per container instead.
import { chargenvorgabeProblem, mengeProblem, portionProblem, produktartOf } from "./angebot";

export type EvidenceKind = "mail" | "attachment" | "activity";

/** Where a value comes from: a mail, an attachment or a note of the deal, with a short quote. */
export interface Evidence {
  kind: EvidenceKind;
  id: string;
  quote: string | null;
}

export interface FieldValue {
  value: string | null;
  evidence: Evidence | null;
  /** Set by a human; a new extraction keeps it. */
  manual?: boolean;
}

export interface Substance {
  name: string | null;
  dose: number | null;
  unit: string | null;
  evidence: Evidence | null;
}

/**
 * The batch size an authorisation, the customer or a process validation sets (agreed with produkt-d5, 02.11.2026):
 * required for every product made that is not NEM, once Ange.Bot takes it (setting ange_bot_chargenvorgabe).
 */
export interface Chargenvorgabe {
  /** true: set; false: expressly none; null: not known yet. */
  vorgegeben: boolean | null;
  /** Per batch, at least one of the two with vorgegeben. */
  min: number | null;
  max: number | null;
  einheit: "kg" | "Stück" | null;
  chargenJeKampagne: number | null;
  quelle: "Zulassung" | "Kundenvorgabe" | "Prozessvalidierung" | null;
  evidence: Evidence | null;
  /** What a human gives as evidence when there is no source to quote. */
  beleg?: string | null;
  manual?: boolean;
}

/** herstellen: Valphira makes the product; nur_verpacken: it packs the bulk the customer supplies (F13). */
export type Leistung = "herstellen" | "nur_verpacken";

export interface Quantity {
  amount: number | null;
  unit: string | null;
  evidence: Evidence | null;
  manual?: boolean;
}

export interface Position {
  name: string;
  /** Missing in positions stored before F13: herstellen. */
  leistung?: { value: Leistung; evidence: Evidence | null; manual?: boolean };
  darreichungsform: FieldValue;
  substanzen: Substance[];
  substanzenManual?: boolean;
  /** With nur_verpacken the bulk the customer delivers. */
  menge: Quantity;
  /** Only with nur_verpacken: the fill quantity per container (60 capsules per tin, 5 g per sachet). */
  portion?: Quantity;
  lieferform: FieldValue;
  chargen: FieldValue;
  /**
   * How the customer splits the order into orders, deliveries or lots, as one sentence without people (at most 300
   * characters; agreed with Ange.Bot 02.11.2026). Missing in positions stored before.
   */
  lieferstruktur?: FieldValue;
  produktart: FieldValue;
  formSpezifikation: FieldValue;
  verpackung: FieldValue;
  /** Missing in positions stored before 02.11.2026. */
  chargenvorgabe?: Chargenvorgabe;
  /** Whether the quantity is delivered once or needed over a year (operator chat-526); unknown: null or missing. */
  mengenart?: { value: Mengenart | null; evidence: Evidence | null; manual?: boolean };
  /** Pieces per sales unit (VPE, operator chat-533): 30 for "Etui mit 30 Tabletten"; never a yearly or order quantity. */
  vpe?: { value: number | null; evidence: Evidence | null; manual?: boolean };
}

/** A VPE Ange.Bot takes: a whole number from 1 to 1,000,000 (produkt-6f); anything else is not sent. */
export const vpeValid = (v: unknown): v is number => typeof v === "number" && Number.isInteger(v) && v >= 1 && v <= 1_000_000;

/** "bestellung": a quantity delivered once; "jahresbedarf": a quantity over a year, also in several lots (Ange.Bot F18). */
export const MENGENARTEN = ["bestellung", "jahresbedarf"] as const;
export type Mengenart = (typeof MENGENARTEN)[number];

export interface Requirements {
  positions: Position[];
  /** Draft query to the customer when required fields are missing. */
  rueckfrage: string | null;
  note: string | null;
}

/** ok: value with evidence; pruefen: value without evidence; fehlt: required and missing; offen: optional and missing. */
export type FieldState = "ok" | "pruefen" | "fehlt" | "offen";

export const OPTIONAL_FIELDS = ["lieferform", "chargen", "lieferstruktur", "produktart", "formSpezifikation", "verpackung"] as const;
export type OptionalField = (typeof OPTIONAL_FIELDS)[number];

export const FIELD_LABELS = {
  leistung: "Leistung",
  darreichungsform: "Darreichungsform",
  substanzen: "Substanzen mit Dosis",
  menge: "Menge",
  portion: "Portion je Gebinde",
  lieferform: "Lieferform",
  chargen: "Herstellchargen",
  lieferstruktur: "Lieferaufteilung",
  produktart: "Produktart",
  formSpezifikation: "Form-Spezifikation",
  verpackung: "Verpackung",
  chargenvorgabe: "Chargenvorgabe",
  mengenart: "Mengenart",
  vpe: "VPE (Stück je Verkaufseinheit)",
} as const;

export interface PositionCheck {
  states: Record<keyof typeof FIELD_LABELS, FieldState>;
  /** Labels of the required fields that block Ange.Bot. */
  missing: string[];
  /** What Ange.Bot cannot take as it is, though complete (a quantity too exact for its units, a portion that does not fit); blocks the push. */
  blocked: string[];
  /** Violations of our own rules (spec 3.1): no-go substance, below the minimum batch. */
  flags: string[];
  /** Estimated bulk mass per batch, if the quantity allows it. */
  batchKg: number | null;
}

export interface RuleSet {
  noGo: readonly string[];
  minBatchKg: number;
  defaultFillMg: Readonly<Record<string, number>>;
  /** Ange.Bot takes "nur verpacken" (F13, its delivery L5; setting ange_bot_f13). Until then such positions wait. */
  packOnly?: boolean;
  /**
   * Ange.Bot takes the batch size (setting ange_bot_chargenvorgabe): a product made needs its product type, and one
   * that is not NEM its batch size (operator, chat-494). Packaging only may carry it, never needs it.
   */
  chargenvorgabe?: boolean;
}

/** Shown at a packaging position while Ange.Bot does not take them yet. */
export const PACK_ONLY_WAITING = "Ange.Bot nimmt „nur verpacken“ noch nicht an (Lieferung L5 von Ange.Bot steht aus); die Position wartet.";

const state = (has: boolean, evidence: unknown, required: boolean): FieldState => (has ? (evidence ? "ok" : "pruefen") : required ? "fehlt" : "offen");

const substanceComplete = (s: Substance) => !!s.name?.trim() && typeof s.dose === "number" && s.dose > 0 && !!s.unit?.trim();
const quantity = (q: Quantity | undefined, required: boolean) => state(typeof q?.amount === "number" && q.amount > 0 && !!q.unit?.trim(), q?.evidence ?? q?.manual, required);

/** Positions stored before F13 have no service: they are made. */
export const leistungOf = (p: Position): Leistung => p.leistung?.value ?? "herstellen";

/** Bulk mass per batch: kg as given, or pieces × default fill weight of the dosage form. */
export function batchMassKg(p: Position, rules: RuleSet): number | null {
  const amount = p.menge.amount;
  if (typeof amount !== "number" || amount <= 0) return null;
  const batches = Math.max(1, Number.parseInt(p.chargen.value ?? "", 10) || 1);
  const unit = (p.menge.unit ?? "").toLowerCase();
  if (unit === "kg") return amount / batches;
  if (unit === "g") return amount / 1000 / batches;
  if (unit === "t") return (amount * 1000) / batches;
  const form = (p.darreichungsform.value ?? "").toLowerCase();
  // The most specific form wins: "Lutschtablette" over "Tablette" (jsonb hands the keys out shortest first).
  const fill = Object.entries(rules.defaultFillMg)
    .filter(([k]) => form.includes(k.toLowerCase()))
    .sort(([a], [b]) => b.length - a.length)[0]?.[1];
  if (!fill || !/^(stück|stk|st|kapseln?|tabletten?|pcs|pieces?)\.?$/i.test(unit)) return null;
  return (amount * fill) / 1e6 / batches;
}

export function checkPosition(p: Position, rules: RuleSet): PositionCheck {
  const subs = p.substanzen ?? [];
  // Packaging only: form and substances are information about what is packed, never missing.
  const packOnly = leistungOf(p) === "nur_verpacken";
  const states: PositionCheck["states"] = {
    leistung: p.leistung?.evidence || p.leistung?.manual ? "ok" : packOnly ? "pruefen" : "offen",
    darreichungsform: state(!!p.darreichungsform.value?.trim(), p.darreichungsform.evidence ?? p.darreichungsform.manual, !packOnly),
    substanzen:
      subs.length && subs.every(substanceComplete) ? (subs.every((s) => s.evidence) || p.substanzenManual ? "ok" : "pruefen") : !packOnly ? "fehlt" : subs.length ? "pruefen" : "offen",
    menge: quantity(p.menge, true),
    portion: quantity(p.portion, packOnly),
    lieferform: state(!!p.lieferform.value?.trim(), p.lieferform.evidence ?? p.lieferform.manual, false),
    chargen: state(!!p.chargen.value?.trim(), p.chargen.evidence ?? p.chargen.manual, false),
    lieferstruktur: state(!!p.lieferstruktur?.value?.trim(), p.lieferstruktur?.evidence ?? p.lieferstruktur?.manual, false),
    mengenart: state(!!p.mengenart?.value, p.mengenart?.evidence ?? p.mengenart?.manual, false),
    vpe: state(vpeValid(p.vpe?.value), p.vpe?.evidence ?? p.vpe?.manual, false),
    produktart: state(!!p.produktart.value?.trim(), p.produktart.evidence ?? p.produktart.manual, false),
    formSpezifikation: state(!!p.formSpezifikation.value?.trim(), p.formSpezifikation.evidence ?? p.formSpezifikation.manual, false),
    verpackung: state(!!p.verpackung.value?.trim(), p.verpackung.evidence ?? p.verpackung.manual, false),
    chargenvorgabe: "offen",
  };
  // An empty or unknown product type is unresolved, never NEM: it is asked for first, the batch size after it.
  const asks = rules.chargenvorgabe === true && !packOnly;
  const art = produktartOf(p.produktart.value);
  const cv = p.chargenvorgabe;
  if (asks) states.produktart = state(art !== null, p.produktart.evidence ?? p.produktart.manual, true);
  if (rules.chargenvorgabe === true) {
    const needed = asks && art !== null && art !== "NEM";
    states.chargenvorgabe = state(cv?.vorgegeben === true || cv?.vorgegeben === false, cv?.evidence ?? cv?.manual ?? (cv?.beleg ? true : null), needed);
  }
  const required = packOnly
    ? (["menge", "portion"] as const)
    : (["darreichungsform", "substanzen", "menge", ...(asks ? (["produktart", "chargenvorgabe"] as const) : [])] as const);
  const missing = required.filter((k) => states[k] === "fehlt").map((k) => FIELD_LABELS[k]);

  const flags: string[] = [];
  const terms = rules.noGo.flatMap((n) => n.split("/")).map((n) => n.trim().toLowerCase()).filter((n) => n.length >= 3);
  for (const s of subs) {
    const hit = terms.find((t) => (s.name ?? "").toLowerCase().includes(t));
    if (hit) flags.push(`No-Go-Stoff: ${s.name}`);
  }
  const batchKg = batchMassKg(p, rules);
  if (batchKg !== null && batchKg < rules.minBatchKg) {
    flags.push(`Unter der Mindestmenge: ≈ ${batchKg.toLocaleString("de-DE", { maximumFractionDigits: 1 })} kg je Charge (mindestens ${rules.minBatchKg} kg)`);
  }
  const blocked = [
    states.menge === "fehlt" ? null : mengeProblem(p.menge.amount, p.menge.unit),
    packOnly && !rules.packOnly ? PACK_ONLY_WAITING : null,
    packOnly && states.menge !== "fehlt" && states.portion !== "fehlt" ? portionProblem(p.menge, p.portion!, p.chargen.value) : null,
    // A batch size that would be sent but is incomplete: Ange.Bot refuses the whole push for it. Where it is not
    // needed (NEM, packaging) an incomplete one is left out of the push instead.
    states.chargenvorgabe === "fehlt" || !(asks && art !== null && art !== "NEM") || !cv ? null : chargenvorgabeProblem(cv),
  ].filter((x) => x !== null);
  return { states, missing, blocked, flags, batchKg };
}

/** A position with its required fields in a form Ange.Bot takes: it may be pushed and checked for feasibility. */
export function completeForAngeBot(p: Position, rules: RuleSet): boolean {
  const c = checkPosition(p, rules);
  return c.missing.length === 0 && c.blocked.length === 0;
}

/** Ready for the push when every position is complete (Ange.Bot's gate decides finally). */
export function readyForAngeBot(r: Requirements | null, rules: RuleSet): boolean {
  return !!r?.positions.length && r.positions.every((p) => completeForAngeBot(p, rules));
}

/**
 * The switch ange_bot_push (setting): true for every deal, false (default) for none, or { deals: [ids or reference
 * numbers] } for the deals of the end-to-end test only (decision with the Ange.Bot coordinator, 31.10.2026).
 */
export function pushSwitchOn(setting: unknown, deal: { id: string; ref: string | null }): boolean {
  if (setting === true) return true;
  const deals = typeof setting === "object" && setting !== null ? (setting as { deals?: unknown }).deals : null;
  const listed = Array.isArray(deals) ? deals.map((x) => String(x).trim().toLowerCase()) : [];
  // An entry may be the id (any case) or the reference number.
  return listed.includes(deal.id.toLowerCase()) || (!!deal.ref && listed.includes(deal.ref.toLowerCase()));
}

/**
 * Keeps what a human has set when a new extraction arrives: positions match by name, else by product, manual fields
 * stay, positions the AI no longer sees but a human has edited stay too.
 */
export function mergeRequirements(previous: Requirements | null, next: Requirements): Requirements {
  if (!previous) return next;
  const key = (n: string) => n.trim().toLowerCase();
  // The AI may rename a position ("Magnesium-Kapseln" → "Magnesium Kapseln 300 mg"): the same form and substances
  // with their doses are the same product, so the human values stay with it and no second position appears. Packaging
  // has no recipe: there the form and the portion are the product (as at Ange.Bot, name plus portion).
  const product = (p: Position) =>
    leistungOf(p) === "nur_verpacken"
      ? typeof p.portion?.amount === "number"
        ? ["nur_verpacken", key(p.darreichungsform.value ?? ""), `${p.portion.amount} ${key(p.portion.unit ?? "")}`].join("|")
        : null
      : (p.substanzen ?? []).length
        ? [key(p.darreichungsform.value ?? ""), ...p.substanzen.map((x) => `${key(x.name ?? "")} ${x.dose ?? ""} ${key(x.unit ?? "")}`).sort()].join("|")
        : null;
  const taken = new Set<Position>();
  const matched = new Map<Position, Position>();
  for (const p of next.positions) {
    const old = previous.positions.find((o) => !taken.has(o) && key(o.name) === key(p.name));
    if (!old) continue;
    taken.add(old);
    matched.set(p, old);
  }
  // By product only when it is unambiguous: two variants of one product must not swap their human values.
  const rest = next.positions.filter((p) => !matched.has(p));
  for (const p of rest) {
    const fp = product(p);
    const olds = fp ? previous.positions.filter((o) => !taken.has(o) && product(o) === fp) : [];
    if (olds.length !== 1 || rest.filter((q) => product(q) === fp).length !== 1) continue;
    taken.add(olds[0]!);
    matched.set(p, olds[0]!);
  }
  const merged = next.positions.map((p) => {
    const old = matched.get(p);
    if (!old) return p;
    const out: Position = { ...p };
    if (old.leistung?.manual) out.leistung = old.leistung;
    if (old.darreichungsform.manual) out.darreichungsform = old.darreichungsform;
    if (old.menge.manual) out.menge = old.menge;
    if (old.portion?.manual) out.portion = old.portion;
    if (old.substanzenManual) {
      out.substanzen = old.substanzen;
      out.substanzenManual = true;
    }
    for (const f of OPTIONAL_FIELDS) if (old[f]?.manual) out[f] = old[f]!;
    if (old.chargenvorgabe?.manual) out.chargenvorgabe = old.chargenvorgabe;
    if (old.mengenart?.manual) out.mengenart = old.mengenart;
    if (old.vpe?.manual) out.vpe = old.vpe;
    return out;
  });
  const isManual = (p: Position) =>
    p.leistung?.manual ||
    p.darreichungsform.manual ||
    p.menge.manual ||
    p.portion?.manual ||
    p.substanzenManual ||
    p.chargenvorgabe?.manual ||
    p.mengenart?.manual ||
    p.vpe?.manual ||
    OPTIONAL_FIELDS.some((f) => p[f]?.manual);
  for (const old of previous.positions) {
    if (isManual(old) && !taken.has(old)) merged.push(old);
  }
  return { ...next, positions: merged };
}
